import { ArrowRightIcon, AtIcon, ClockIcon, PenIcon, PhoneIcon } from "@xola/icons";
import { Badge, flash, Phone, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { DateFormat, formatDate } from "common";
import { sortBy } from "lodash";
import React, { ComponentProps } from "react";
import { useMutation } from "react-query";
import { Delegate, DelegatePermission, DelegateRole, Permission, UserRolesCopy } from "types";
import { api } from "../../../api/api";
import { IsGranted } from "../../Common/IsGranted";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { RevokeDelegateAccessButton } from "./RevokeDelegateAccessButton";

export const DelegateItem = ({ delegate, onEdit }: { delegate: Delegate; onEdit: (values: Delegate) => void }) => {
    const { user } = delegate;

    const { mutateAsync: resendInvite } = useMutation(async (delegateId: ID) => {
        return api.get(`/users/${delegateId}/resendConfirmation`);
    });

    const handleResendInvite = async () => {
        await resendInvite(user.id);
        flash.success(`Email confirmation re-sent to ${user.email}`);
    };

    const isSellerAdmin = delegate.permissions.some((permission) => permission.name === DelegateRole.SELLER_ADMIN);

    return (
        <div
            className={clsx("flex w-full flex-col justify-between space-y-2 py-7 hover:bg-gray-hover")}
            data-testid="delegate-item"
        >
            <div className="flex w-full items-start justify-between">
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                    <div className="min-w-0 truncate text-lg font-bold" data-testid="delegate-item-user-name">
                        {user.name ? <NoTranslate>{user.name}</NoTranslate> : <span>Name not set</span>}
                    </div>

                    <div>{isSellerAdmin && <Badge color="critical">Admin</Badge>}</div>

                    {!user.enabled && (
                        <Badge color="warning" size="small" data-testid="delegate-item-status">
                            Pending Email Verification
                        </Badge>
                    )}
                </div>

                <div className="flex shrink-0 flex-nowrap items-center space-x-2 whitespace-nowrap">
                    {!user.enabled && (
                        <SecondaryButton
                            size="small"
                            data-testid="delegate-item-resend-invite-revoke-access-button"
                            className="whitespace-nowrap"
                            onClick={handleResendInvite}
                        >
                            Resend Invite
                        </SecondaryButton>
                    )}

                    <IsGranted permissions={Permission.MODIFY_ACCESS_MANAGER_REVOKE_ACCESS}>
                        <div className="whitespace-nowrap">
                            <RevokeDelegateAccessButton delegate={delegate} />
                        </div>
                    </IsGranted>

                    <SecondaryButton
                        size="small"
                        data-testid="delegate-item-edit-button"
                        className="whitespace-nowrap"
                        onClick={() => onEdit(delegate)}
                    >
                        <PenIcon />
                    </SecondaryButton>
                </div>
            </div>

            <div className="flex items-center space-x-1">
                <AtIcon className="relative top-0" />
                <Email data-testid="delegate-item-user-email">{user.email}</Email>
                {user.newEmail && (
                    <>
                        <ArrowRightIcon />
                        <Email className="text-warning-dark" data-testid="delegate-item-new-user-email">
                            {user.newEmail}
                        </Email>
                        <Badge color="warning" size="small">
                            Pending Confirmation
                        </Badge>
                    </>
                )}
            </div>

            <div className="flex w-full flex-row justify-between">
                <div className="w-full space-y-1">
                    {user.phone && (
                        <NoTranslate
                            as="div"
                            className="flex items-center space-x-1"
                            data-testid="delegate-item-user-phone"
                        >
                            <PhoneIcon />
                            <Phone>{user.phone}</Phone>
                        </NoTranslate>
                    )}
                    <PermissionBadges permissions={delegate.permissions} />
                </div>
                <div
                    className="flex shrink-0 items-end text-sm text-gray-darker"
                    data-testid="delegate-item-user-last-login"
                >
                    {user.lastLogin && (
                        <Tooltip placement="top" content="Last login time">
                            <div className="flex items-center space-x-1">
                                <ClockIcon />
                                <NoTranslate>{formatDate(user.lastLogin, DateFormat.DATE_TIME)}</NoTranslate>
                            </div>
                        </Tooltip>
                    )}
                </div>
            </div>
        </div>
    );
};

const Email = ({ className, children, ...rest }: ComponentProps<"div">) => {
    return (
        <NoTranslate as="div" className={clsx("text-base underline underline-offset-1", className)} {...rest}>
            {children}
        </NoTranslate>
    );
};

const PermissionBadges = ({ permissions }: { permissions: DelegatePermission[] }) => {
    const permissionList = sortBy(permissions, "name");

    return (
        <div className="flex flex-wrap gap-2" data-testid="delegate-item-roles">
            {permissionList.map((permission) => (
                <Badge key={permission.name} color="secondary" size="small">
                    {UserRolesCopy[permission.name as keyof typeof UserRolesCopy]}
                </Badge>
            ))}
        </div>
    );
};
